import { getBillingDetails } from "api/billing";
import { ApiError } from "api/client";
import {
	cancelSubscription,
	confirmSubscription,
	createSubscription,
	getSubscriptionPlans,
	getSubscriptionStatus
} from "api/subscriptions";
import type { BillingDetailsDto, SubscriptionPlanDto, SubscriptionStatusDto } from "api/types";
import { useBrand } from "hooks/useBrand";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Paths from "routes/Paths";
import { useSession } from "Session";
import { formatAmount, formatPrice, openRazorpayCheckout } from "utilities";

import BillingDetailsDialog from "./BillingDetailsDialog";
import FreePlanCard from "./FreePlanCard";
import ProPlanCard from "./ProPlanCard";

import styles from "./PlansPage.module.css";

/** Error code the backend raises when checkout starts without a saved billing address. */
const BILLING_DETAILS_REQUIRED = "BILLING_DETAILS_REQUIRED";

/** Picks the tax-note wording: CGST + SGST for intra-state, IGST for inter-state, generic GST when unknown. */
const taxNoteKey = (isIntraState?: boolean | null): string => {
	if (isIntraState === true) {
		return "plusGstIntra";
	}

	if (isIntraState === false) {
		return "plusGstInter";
	}

	return "plusGst";
};

const formatDate = (iso?: string | null): string => {
	if (!iso) {
		return "";
	}

	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) {
		return "";
	}

	return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

/**
 * The Pro plans page: shows the single monthly plan, collects billing details before the first
 * checkout (Razorpay requires an address on file), then runs the Razorpay checkout and confirms it.
 */
const PlansPage = () => {
	const { t } = useTranslation("plansPage");
	const { brand } = useBrand();
	const navigate = useNavigate();
	const { getAccessToken, signOut, user } = useSession();
	const [plans, setPlans] = useState<SubscriptionPlanDto[]>([]);
	const [subscription, setSubscription] = useState<SubscriptionStatusDto | null>(null);
	const [billingDetails, setBillingDetails] = useState<BillingDetailsDto | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isSubscribing, setIsSubscribing] = useState(false);
	const [isCancelling, setIsCancelling] = useState(false);
	const [isBillingDialogOpen, setIsBillingDialogOpen] = useState(false);
	const [errorMessage, setErrorMessage] = useState("");
	const [successMessage, setSuccessMessage] = useState("");

	useEffect(() => {
		let isCurrent = true;

		const load = async (): Promise<void> => {
			try {
				const token = await getAccessToken();
				if (!token || !isCurrent) {
					return;
				}

				const [planList, status, billing] = await Promise.all([
					getSubscriptionPlans(token),
					getSubscriptionStatus(token),
					getBillingDetails(token)
				]);

				if (!isCurrent) {
					return;
				}

				setPlans(planList);
				setSubscription(status);
				setBillingDetails(billing);
			} catch (error) {
				if (isCurrent && error instanceof ApiError && error.status === 401) {
					signOut();
				}
			} finally {
				if (isCurrent) {
					setIsLoading(false);
				}
			}
		};

		load().catch(() => undefined);

		return () => {
			isCurrent = false;
		};
	}, [getAccessToken, signOut]);

	const startCheckout = useCallback(async (): Promise<void> => {
		setErrorMessage("");
		setSuccessMessage("");
		setIsSubscribing(true);
		try {
			const token = await getAccessToken();
			if (!token) {
				return;
			}

			const checkout = await createSubscription(token);
			const payload = await openRazorpayCheckout(checkout, {
				description: t("checkout.description", { brand }),
				dismissed: t("checkout.dismissed"),
				// Razorpay shows this as the merchant name, so it is the bare brand.
				name: brand,
				scriptError: t("checkout.scriptError"),
				unavailable: t("checkout.unavailable")
			});
			const status = await confirmSubscription(payload, token);
			setSubscription(status);
			setSuccessMessage(t("paymentSuccess"));
		} catch (error) {
			// Backend safety net: route to the billing form and resume checkout after saving.
			if (error instanceof ApiError && error.message === BILLING_DETAILS_REQUIRED) {
				setIsBillingDialogOpen(true);

				return;
			}

			setErrorMessage(error instanceof Error ? error.message : t("genericError"));
		} finally {
			setIsSubscribing(false);
		}
	}, [brand, getAccessToken, t]);

	const onSubscribe = useCallback((): void => {
		// Billing details gate the first purchase; collect them before opening the checkout.
		if (!billingDetails) {
			setIsBillingDialogOpen(true);

			return;
		}

		startCheckout().catch(() => undefined);
	}, [billingDetails, startCheckout]);

	const closeBillingDialog = useCallback((): void => {
		setIsBillingDialogOpen(false);
	}, []);

	const onBillingSaved = useCallback(
		(saved: BillingDetailsDto): void => {
			setBillingDetails(saved);
			setIsBillingDialogOpen(false);
			startCheckout().catch(() => undefined);
		},
		[startCheckout]
	);

	const onCancelAutoRenew = useCallback(async (): Promise<void> => {
		// eslint-disable-next-line no-alert
		if (isCancelling || !window.confirm(t("confirmCancel"))) {
			return;
		}

		setErrorMessage("");
		setIsCancelling(true);
		try {
			const token = await getAccessToken();
			if (!token) {
				return;
			}

			const status = await cancelSubscription(token);
			setSubscription(status);
		} catch (error) {
			setErrorMessage(error instanceof Error ? error.message : t("genericError"));
		} finally {
			setIsCancelling(false);
		}
	}, [getAccessToken, isCancelling, t]);

	const onBack = useCallback((): void => navigate(Paths.root), [navigate]);

	const plan = plans[0] ?? null;
	const isPro = subscription?.isPro ?? false;
	const autoRenewOn = isPro && subscription?.autoRenew !== false;
	const renewalDate = formatDate(subscription?.currentPeriodEnd ?? subscription?.renewalDate);
	// Headline shows the GST-exclusive base price (back-calculated from the GST-inclusive Razorpay
	// plan amount); the tax note shows the contained GST (split as CGST + SGST or IGST by the saved
	// billing state) and the debited total.
	const priceAmount = plan ? formatPrice(plan.baseAmount || plan.amount, plan.currency) : null;
	const taxNote =
		plan && plan.taxAmount > 0
			? t(taxNoteKey(plan.isIntraState), {
					gstAmount: formatPrice(plan.taxAmount, plan.currency),
					gstRate: formatAmount(plan.taxRate),
					halfRate: formatAmount(plan.taxRate / 2),
					total: formatPrice(plan.amount, plan.currency)
			  })
			: "";

	return (
		<div className={styles.page}>
			<header className={styles.topBar}>
				<button className={styles.backButton} onClick={onBack} type="button">
					<span aria-hidden="true">←</span>
					<span>{t("backToTrees")}</span>
				</button>
				<div className={styles.brand}>
					<img alt={brand} src={`${process.env.PUBLIC_URL}/assets/images/logo.png`} />
					<span>{brand}</span>
				</div>
			</header>

			<main className={styles.content}>
				<h1 className={styles.title}>{t("title")}</h1>
				<p className={styles.subtitle}>{t("subtitle")}</p>

				{isLoading ? (
					<div className={styles.loadingState}>…</div>
				) : (
					<div className={styles.plansGrid}>
						<FreePlanCard isCurrent={!isPro} />
						<ProPlanCard
							autoRenewOn={autoRenewOn}
							isCancelling={isCancelling}
							isCurrent={isPro}
							isSubscribing={isSubscribing}
							onCancelAutoRenew={onCancelAutoRenew}
							onSubscribe={onSubscribe}
							planDescription={plan?.description ?? ""}
							planName={plan?.name ?? t("pro.name")}
							priceAmount={priceAmount}
							renewalDate={renewalDate}
							taxNote={taxNote}
						/>
					</div>
				)}

				{errorMessage && <div className={styles.errorMessage}>{errorMessage}</div>}
				{successMessage && <div className={styles.successMessage}>{successMessage}</div>}
			</main>

			{isBillingDialogOpen && (
				<BillingDetailsDialog
					defaultCustomerName={user?.fullName ?? ""}
					initialDetails={billingDetails}
					onClose={closeBillingDialog}
					onSaved={onBillingSaved}
				/>
			)}
		</div>
	);
};

export default PlansPage;
